/**
 * Centralized Error Handling Utility
 * Provides consistent, user-friendly error messages throughout the application
 */

import logger from "./logger";

/** Firebase Auth — codes as returned on error.code */
export const FIREBASE_AUTH_ERROR_MESSAGES = Object.freeze({
  "auth/user-not-found": "No account found with this email address.",
  "auth/wrong-password": "Incorrect password. Please try again.",
  "auth/invalid-email": "Invalid email address format.",
  "auth/user-disabled": "This account has been disabled. Please contact support.",
  "auth/email-already-in-use": "An account with this email already exists.",
  "auth/weak-password": "Password is too weak. Please choose a stronger password.",
  "auth/operation-not-allowed": "This operation is not allowed. Please contact support.",
  "auth/too-many-requests": "Too many attempts. Please try again in a few minutes.",
  "auth/network-request-failed": "Network error. Check your connection and try again.",
  "auth/invalid-credential": "Invalid email or password. Please check your credentials.",
  "auth/requires-recent-login": "Please sign in again to complete this action.",
  "auth/user-token-expired": "Your session has expired. Please sign in again.",
  "auth/popup-closed-by-user": "Sign-in was cancelled. Try again when you’re ready.",
  "auth/cancelled-popup-request": "Another sign-in is already in progress.",
  "auth/account-exists-with-different-credential": "An account already exists with a different sign-in method.",
});

/** Firebase Realtime Database / generic Firebase client codes */
const FIREBASE_DATA_ERROR_MESSAGES = Object.freeze({
  PERMISSION_DENIED: "You don’t have permission to do that. If you’re signed in, try again or contact support.",
  permission_denied: "You don’t have permission to do that. If you’re signed in, try again or contact support.",
  "permission-denied": "You don’t have permission to do that. If you’re signed in, try again or contact support.",
  UNAVAILABLE: "Our servers are temporarily unavailable. Please try again shortly.",
  unavailable: "Our servers are temporarily unavailable. Please try again shortly.",
  DISCONNECTED: "You appear to be offline. Check your connection and try again.",
  "auth/network-request-failed": "Network error. Check your connection and try again.",
});

/**
 * Firebase Auth errors (error.code like auth/...)
 */
export function formatFirebaseAuthError(error, defaultMessage = "Sign-in failed. Please try again.") {
  if (!error) return defaultMessage;
  if (typeof error === "string") return error;
  const code = error.code || "";
  if (code && FIREBASE_AUTH_ERROR_MESSAGES[code]) return FIREBASE_AUTH_ERROR_MESSAGES[code];
  const msg = String(error.message || "");
  for (const [key, text] of Object.entries(FIREBASE_AUTH_ERROR_MESSAGES)) {
    if (msg.includes(key)) return text;
  }
  if (msg && msg.length > 0 && msg.length < 240 && !msg.includes("Firebase")) return msg;
  return defaultMessage;
}

function formatFirebaseDataError(error, defaultMessage) {
  if (!error) return defaultMessage;
  const code = error.code || error?.status || "";
  const s = String(code);
  if (FIREBASE_DATA_ERROR_MESSAGES[s]) return FIREBASE_DATA_ERROR_MESSAGES[s];
  if (s.toLowerCase().includes("permission") || msgIncludes(error, "permission denied")) {
    return FIREBASE_DATA_ERROR_MESSAGES.permission_denied;
  }
  if (s.toLowerCase().includes("network") || msgIncludes(error, "network")) {
    return "Network error. Check your connection and try again.";
  }
  if (msgIncludes(error, "index")) {
    return "This action couldn’t complete due to a server configuration issue. Please try again later.";
  }
  return null;
}

function msgIncludes(error, needle) {
  return String(error?.message || "").toLowerCase().includes(needle);
}

/**
 * Formats error messages for display to users
 * @param {Error|string|object} error - The error object or message
 * @param {string} defaultMessage - Default message if error parsing fails
 * @returns {string} User-friendly error message
 */
export const formatErrorMessage = (error, defaultMessage = "An unexpected error occurred. Please try again.") => {
  if (!error) return defaultMessage;

  // If it's already a string, return it
  if (typeof error === "string") {
    return error;
  }

  // Firebase Auth
  if (error?.code && String(error.code).startsWith("auth/")) {
    return formatFirebaseAuthError(error, defaultMessage);
  }

  // Firebase Database / other client codes (often permission-denied, etc.)
  const dataMsg = formatFirebaseDataError(error, defaultMessage);
  if (dataMsg) return dataMsg;

  // Handle API error responses
  if (error?.response?.data) {
    const data = error.response.data;
    
    // Prefer user-friendly message if available
    if (data.message) return data.message;
    if (data.error) return data.error;
    if (data.errorMessage) return data.errorMessage;
    
    // Handle validation errors
    if (data.errors && Array.isArray(data.errors)) {
      return data.errors.join(", ");
    }
    
    // Handle object with error messages
    if (typeof data === "object" && !Array.isArray(data)) {
      const messages = Object.values(data).filter(msg => typeof msg === "string");
      if (messages.length > 0) return messages.join(", ");
    }
  }

  // Handle error objects with message property
  if (error?.message) {
    return error.message;
  }

  // Handle network errors
  if (error?.code === "NETWORK_ERROR" || error?.message?.includes("Network")) {
    return "Network error. Please check your internet connection and try again.";
  }

  // Handle timeout errors
  if (error?.code === "ECONNABORTED" || error?.message?.includes("timeout")) {
    return "Request timed out. Please try again.";
  }

  // Handle 401 Unauthorized
  if (error?.response?.status === 401) {
    return "Your session has expired. Please log in again.";
  }

  // Handle 403 Forbidden
  if (error?.response?.status === 403) {
    return "You don't have permission to perform this action.";
  }

  // Handle 404 Not Found
  if (error?.response?.status === 404) {
    return "The requested resource was not found.";
  }

  // Handle 500 Server Error
  if (error?.response?.status === 500) {
    return "Server error. Please try again later or contact support if the problem persists.";
  }

  // Handle 503 Service Unavailable
  if (error?.response?.status === 503) {
    return "Service temporarily unavailable. Please try again in a few moments.";
  }

  // Default fallback
  return defaultMessage;
};

/**
 * Logs errors for debugging
 * @param {Error|string|object} error - The error to log
 * @param {string} context - Context where the error occurred
 */
export const logError = (error, context = "Unknown") => {
  const additionalData = {};
  
  // Add API response details if available
  if (error?.response) {
    additionalData.apiResponse = {
      status: error.response.status,
      data: error.response.data,
      headers: error.response.headers,
    };
  }
  
  // Use centralized logger
  logger.error(error, context, additionalData);
};

/**
 * Creates a user-friendly error handler for async operations
 * @param {Function} asyncFn - Async function to wrap
 * @param {string} context - Context for error logging
 * @param {string} defaultMessage - Default error message
 * @returns {Function} Wrapped function with error handling
 */
export const withErrorHandling = (asyncFn, context = "Operation", defaultMessage = null) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      logError(error, context);
      const message = formatErrorMessage(error, defaultMessage || `${context} failed. Please try again.`);
      throw new Error(message);
    }
  };
};

/**
 * User-facing copy for HTTP failures thrown by `src/api`.
 * 401 and timeout wording matches the checkout screen; other statuses keep the API message.
 * @param {Error|{ message?: string, status?: number }|string} error
 * @param {string} [fallback]
 */
export function formatRequestError(error, fallback = "Something went wrong. Please try again.") {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  const status = error.status;
  const msg = error.message || fallback;
  if (status === 401) return "Please sign in again and retry.";
  if (status === 408 || /abort|timed out/i.test(msg)) {
    return "The request timed out. Check your connection and try again.";
  }
  return msg || fallback;
}

/**
 * Error messages for common scenarios
 */
export const ERROR_MESSAGES = {
  NETWORK: "Network error. Please check your internet connection and try again.",
  TIMEOUT: "Request timed out. Please try again.",
  UNAUTHORIZED: "Your session has expired. Please log in again.",
  FORBIDDEN: "You don't have permission to perform this action.",
  NOT_FOUND: "The requested resource was not found.",
  SERVER_ERROR: "Server error. Please try again later or contact support if the problem persists.",
  VALIDATION: "Please check your input and try again.",
  GENERIC: "An unexpected error occurred. Please try again.",
};

